import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountPhotoComponent } from '../../components/account-photo/account-photo.component';
import { MatIconModule } from '@angular/material/icon';
import { FormContainerComponent } from '@olv/ui-shared/ui-shared/form-container/form-container.component';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { AccountService } from '@olv/ui-shared/services/account.service';
import { MerchantAccountService } from '@olv/ui-shared/services/merchant-account.service';
import { CreateAccountDto } from '@olv/ui-shared/models/account.interface';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-add-account',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FormContainerComponent,
    DropdownComponent,
    TextInputComponent,
    MatIconModule,
    LoadingSpinnerComponent,
    ButtonComponent,
  ],
  templateUrl: './add-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-account.component.scss'],
})
export class AddAccountComponent implements OnInit {
  @Input() return = '';
  @Output() closed = new EventEmitter<void>();
  accountCreated = output<void>();

  private fb = inject(FormBuilder);
  private accountService = inject(AccountService);
  private merchantAccountService = inject(MerchantAccountService);
  private router = inject(Router);
  private formErrorService = inject(FormErrorService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  accountForm!: FormGroup;
  loading = false;
  submitted = false;

  roleOptions: { label: string; value: string }[] = [];
  rolesLoading = true;

  photoFile: File | null = null;
  photoPreviewUrl: string | null = null;

  errorMessage = '';

  ngOnInit(): void {
    this.initializeForm();
    this.loadRoleOptions();

    this.accountForm
      .get('email')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.errorMessage) {
          this.errorMessage = '';
        }
      });
  }

  private initializeForm(): void {
    this.accountForm = this.fb.group({
      firstName: ['', [Validators.required, ValidationService.textValidator(2, 100, "^[\\p{L}][\\p{L} .'’-]*$")]],
      lastName: ['', [Validators.required, ValidationService.textValidator(2, 100, "^[\\p{L}][\\p{L} .'’-]*$")]],
      position: ['', [Validators.required, ValidationService.textValidator(2, 100, "^[\\p{L}\\p{N} .,'&/-]*$")]],
      email: ['', [Validators.required, Validators.email]],
      role: [[], [Validators.required]],
    });
  }

  getFieldError(controlName: string, fieldName: string, customMessages?: { [key: string]: string }): string {
    return this.formErrorService.getErrorMessage(this.accountForm.get(controlName), fieldName, customMessages);
  }

  private loadRoleOptions(): void {
    const merchantId = this.route.snapshot.paramMap.get('id');

    const groups$ =
      this.return === 'merchant' && merchantId
        ? this.merchantAccountService.getAccountGroups(merchantId)
        : this.accountService.getAccountGroups();

    groups$.subscribe({
      next: (groups) => {
        this.roleOptions = groups.map((group) => ({
          label: group.name,
          value: group.id,
        }));
        this.rolesLoading = false;
      },
      error: () => {
        this.rolesLoading = false;
      },
    });
  }

  get formControls() {
    return this.accountForm.controls;
  }

  get cancelButtonState(): 'disabled' | 'default' {
    return this.loading ? 'disabled' : 'default';
  }

  get saveButtonState(): 'loading' | 'disabled' | 'default' {
    if (this.loading) return 'loading';
    return this.accountForm.invalid ? 'disabled' : 'default';
  }

  onBack(): void {
    if (this.return === 'merchant') {
      this.closed.emit();
    } else {
      this.router.navigate(['/account-management']);
    }
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.accountForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.loading = true;
    const raw = this.accountForm.value;

    const accountData: CreateAccountDto & { profileFile?: File } = {
      firstName: raw.firstName,
      lastName: raw.lastName,
      position: raw.position,
      email: raw.email,
      groups: raw.role,
      ...(this.photoFile ? { profileFile: this.photoFile } : {}),
    };

    const merchantId = this.route.snapshot.paramMap.get('id');

    if (this.return === 'merchant' && merchantId) {
      // Use merchant-scoped endpoint
      this.merchantAccountService.createAccount(merchantId, accountData).subscribe({
        next: () => {
          this.loading = false;
          this.accountCreated.emit();
          this.closed.emit();
        },
        error: (error) => {
          this.loading = false;
          this.errorMessage = error.message;
        },
      });
    } else {
      // Use regular account endpoint
      this.accountService.createAccount(accountData).subscribe({
        next: () => {
          this.loading = false;
          this.accountService.clearFilters();
          this.router.navigate(['/account-management']);
        },
        error: (error) => {
          this.loading = false;
          this.errorMessage = error.message;
        },
      });
    }
  }

  onCancel(): void {
    this.onBack();
  }

  openPhotoEditor(): void {
    const dialogRef = this.dialog.open(AccountPhotoComponent, {
      width: '600px',
      maxHeight: '95vh',
      maxWidth: '95vw',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((file: File | undefined) => {
      if (!file) return;
      this.onPhotoReady(file);
    });
  }

  onPhotoReady(file: File): void {
    if (this.photoPreviewUrl) {
      URL.revokeObjectURL(this.photoPreviewUrl);
    }
    this.photoFile = file;
    this.photoPreviewUrl = URL.createObjectURL(file);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.accountForm.controls).forEach((key) => {
      const control = this.accountForm.get(key);
      control?.markAsTouched();
    });
  }

  onRoleChange(value: string): void {
    this.accountForm.patchValue({ role: value });
  }
}
